import { useMemo } from "react";
import { Box } from "@mui/material";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { ChartBuilder } from "src/utils/data-convert";
import { DateTypes, ChartType } from "src/utils/configs";

/**
 * 카드 내부용 차트.
 * DataLake response를 받아 ChartBuilder로 Highcharts 옵션을 생성한다.
 *
 * @param {Array<{response: object, name: string, color: string}>} seriesData - DataLake responses
 * @param {string} title - 차트 제목
 * @param {number} height - 차트 높이 (기본 150px)
 * @param {string} yAxisName - Y축 이름
 * @param {number} magnification - 값 배율 (기본 1, 만원→억 변환 시 10000 등)
 */
export const CardChart = ({ seriesData = [], title = "", height = 150, yAxisName = "", magnification = 1, xTickDays = 365, yearRange = null }) => {
  const options = useMemo(() => {
    if (!seriesData || seriesData.length === 0) return null;

    // 유효한 시리즈가 있는지 확인
    const validSeries = seriesData.filter(
      (s) => s.response && s.response.data && s.response.data.length > 0,
    );
    if (validSeries.length === 0) return null;

    const dateType = validSeries[0].response.dateType ?? DateTypes.MONTHLY;
    const builder = new ChartBuilder(title, dateType, height, { xTickDays });
    builder.addNumericYAxis(yAxisName, false, true);

    for (const s of validSeries) {
      const index = s.response.data[0];
      const dates = s.response.dates;
      if (!index || !dates) continue;

      builder.addYValue(index, dates, {
        yAxis: 0,
        name: s.name,
        chartType: ChartType.LINE,
        color: s.color,
        magnification,
        label: false,
        lastLabel: true,
      });
    }

    const opts = builder.build();
    // X축 표시 범위 제한 (yearRange 년 전부터 현재까지)
    if (yearRange && opts.xAxis) {
      opts.xAxis.min = Date.now() - yearRange * 365.25 * 24 * 3600 * 1000;
    }
    // 카드 내부용 컴팩트 스타일 적용
    opts.chart = {
      ...opts.chart,
      height,
      margin: [10, 10, 35, 40],
      backgroundColor: "transparent",
    };
    opts.title = { text: null };
    opts.credits = { enabled: false };
    opts.legend = { enabled: false };
    if (opts.xAxis) {
      opts.xAxis.labels = { ...opts.xAxis.labels, style: { fontSize: "8px" } };
    }
    if (opts.yAxis) {
      const yArr = Array.isArray(opts.yAxis) ? opts.yAxis : [opts.yAxis];
      yArr.forEach((y) => {
        y.labels = { ...y.labels, style: { fontSize: "8px" } };
        y.title = { text: null };
      });
    }
    return opts;
  }, [seriesData, title, height, yAxisName, magnification, xTickDays, yearRange]);

  if (!options) return null;

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        containerProps={{ style: { height: `${height}px` } }}
      />
    </Box>
  );
};

